/* eslint-disable unicorn/filename-case */
import * as microsoftTeams from "@microsoft/teams-js";
import { AccountInfo } from "@azure/msal-browser";
import { logger } from "../../Logger";
import { Configuration } from "../../services/configuration";
import { Constants } from "../../common/constants";
import { RequestUtils } from "../../services/RequestUtils";
import { ApiResponse } from "../../types/ApiRequest";
import { AuthCallResponse, ISAMConfig } from "../../types/CommonTypes";

/**@class: AuthenticationUtils
 * @description: Defines all the methods related to authentication to be used in the frontend application.
 */
export class AuthenticationUtils {
  /**@function authenticateAsync
   * @description method to perform the authentication in asynch mode.
   * @param sessionId {string} Application session id.
   * @param samConfiguration {ISAMConfig} SAM configuration if enabled else undefined.
   * @param conversationId {string} Bot conversation id, used to post the teamcenter code.
   * @param tokenSid {string} Token session id passed to the auth popup and SSO login.
   * @param tcVersion {string} Teamcenter version from configuration.
   * @param userInfo {AccountInfo} Resolved AAD account info (username, localAccountId, etc.).
   * @returns authenticateCall
   */
  public static async authenticateAsync(
    sessionId: string,
    samConfiguration: ISAMConfig,
    conversationId?: string,
    tokenSid?: string,
    tcVersion?: string,
    userInfo?: AccountInfo
  ): Promise<AuthCallResponse> {
    logger.logTrace(`Entered authenticateAsync`);
    const retVal: AuthCallResponse = {
      sessionId: undefined,
      userId: undefined,
      userUid: undefined,
      locale: undefined,
      error: undefined,
      siteId: undefined,
      tcVersion: undefined,
      userEmailId: undefined,
      userDisplayName: undefined,
      aadObjectId: undefined,
      ssoToken: undefined,
    };
    const logContext = logger.createContext({ actionId: "authenticateAsync" });
    try {
      const selectedSite = Configuration.getValueFromLocalStorage(
        Constants.cacheSelectedSite
      );
      const siteId =
        selectedSite && selectedSite.siteId ? selectedSite.siteId : "";
      const siteDisplay =
        selectedSite && selectedSite.siteDisplay
          ? selectedSite.siteDisplay
          : "";
      const appDeploymentId =
        selectedSite && selectedSite.appDeploymentId
          ? selectedSite.appDeploymentId
          : "";
      const appName =
        selectedSite && selectedSite.appName ? selectedSite.appName : "";
      const tcVersionParam = tcVersion ?? "default";
      const userEmail = userInfo?.username ?? "";
      let urlString = `${window.location.origin}/index.html#/teamcenterauthpopup?sessionId=${encodeURIComponent(sessionId)}&siteId=${encodeURIComponent(siteId)}&siteDisplay=${encodeURIComponent(siteDisplay)}&tokenSid=${encodeURIComponent(tokenSid ?? "")}&appDeploymentId=${encodeURIComponent(appDeploymentId)}&appName=${encodeURIComponent(appName)}&tcVersion=${encodeURIComponent(tcVersionParam)}`;
      if (
        samConfiguration &&
        samConfiguration.SAMEnabled &&
        samConfiguration.samLoginUrl
      ) {
        urlString = `${samConfiguration.samLoginUrl}&login_hint=${encodeURIComponent(userEmail)}`;
      }
      const popup: microsoftTeams.authentication.AuthenticatePopUpParameters = {
        url: urlString,
        width: 600,
        height: 535,
      };

      logger.logInformation("authenticateAsync() called.", logContext);

      // microsoftTeams.authentication.notifySuccess(stringCode) is called in the popup window
      // and will return here the code from TeamCenter
      await microsoftTeams.app.initialize();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const teamCenterCodeString: any =
        await microsoftTeams.authentication.authenticate(popup);
      let respObject: any;
      if (typeof teamCenterCodeString === "object") {
        respObject = teamCenterCodeString;
      } else {
        respObject = JSON.parse(teamCenterCodeString);
      }
      respObject.userEmailId = userInfo?.username;

      if (samConfiguration && samConfiguration.SAMEnabled) {
        // For SAM Enabled Authorizations
        await this.initiateSAMEnabledLoginSSO(
          respObject,
          retVal,
          tokenSid,
          userInfo
        );
        var payload = {
          useruid: retVal.userUid,
          tenantId: retVal.tenantId,
          botendpoint: respObject.botendpoint,
        };
        await this.postTeamcenterCode(payload, conversationId, tokenSid);
      } else {
        this.populateAuthCallResponse(retVal, respObject);
        await this.postTeamcenterCode(respObject, conversationId, tokenSid);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      retVal.error = error;
    }
    logger.logInformation("authenticateAsync() completed.", logContext);
    logger.logTrace(`Exit authenticateAsync`);
    return retVal;
  }

  private static populateAuthCallResponse(
    authCallResponse: AuthCallResponse,
    teamcenterCode: any
  ) {
    logger.logTrace(`Entered populateAuthCallResponse`);
    authCallResponse.sessionId = teamcenterCode.code;
    authCallResponse.userId = teamcenterCode.userid;
    authCallResponse.userUid = teamcenterCode.useruid;
    authCallResponse.locale = teamcenterCode.locale;
    authCallResponse.siteId = teamcenterCode.siteId;
    authCallResponse.tcVersion = teamcenterCode.tcVersion;
    authCallResponse.userEmailId = teamcenterCode.userEmailId;
    authCallResponse.userDisplayName = teamcenterCode.userDisplayName;
    authCallResponse.appDeploymentId = teamcenterCode.appDeploymentId;
    logger.logTrace(`Exit populateAuthCallResponse`);
  }

  /**
   * @function postTeamcenterCode
   * @description method to post the teamcenter code to the bot endpoint.
   * @param teamcenterCode {any} teamcenter code object containing useruid, tenantId, and botendpoint.
   * @param conversationId {string} Bot conversation id used to route the update.
   * @param tokenId {string} Token session id included in the payload.
   */
  public static async postTeamcenterCode(
    teamcenterCode: any,
    conversationId: string = "",
    tokenId: string = ""
  ): Promise<void> {
    logger.logTrace(`Entered postTeamcenterCode`);

    if (
      conversationId === "" ||
      tokenId === "" ||
      !teamcenterCode.botendpoint
    ) {
      logger.logTrace(
        `Exit postTeamcenterCode due to missing conversationId or tokenId or botendpoint`
      );
      return;
    }

    const logContext = logger.createContext({ actionId: "postTeamcenterCode" });
    const payload = {
      userUid: teamcenterCode.useruid,
      tenantId: teamcenterCode.tenantId,
      conversationId: conversationId,
      tokenId: tokenId,
    };

    logger.logInformation(`PostTeamcenterCode() called.`, logContext);
    try {
      const rawToken = await microsoftTeams.authentication.getAuthToken();
      const bearerToken = `Bearer ${rawToken}`;
      const response = await fetch(
        `${teamcenterCode.botendpoint}/messages/update-config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // prettier-ignore
            "Authorization": bearerToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        logger.logError(
          `Failed to post teamcenterCode: ${response.statusText}`,
          logContext
        );
        throw new Error(
          `Failed to post teamcenterCode: ${response.statusText}`
        );
      }

      const responseData = await response.json();
      logger.logInformation(`postTeamcenterCode() completed.`, logContext);
      return responseData;
    } catch (error) {
      logger.logError(
        `Error posting teamcenterCode: ${error} for copilot`,
        logContext
      );
    }
    logger.logTrace(`Exit postTeamcenterCode`);
  }

  /**
   * @function checkForSAMAuthentication
   * @description method to check for SAM enabled Authentication from the configuration.
   * @param configuration {any} configuration value from the control plane database.
   * @returns {ISAMConfig} SAM configuration
   */
  public static checkForSAMAuthentication(configuration: any): ISAMConfig {
    logger.logTrace(`Entered checkForSAMAuthentication`);
    let retVal: ISAMConfig;
    const authenticationValue: string =
      Configuration.getConfigurationForAttribute(
        Constants.authorizerType,
        configuration
      );
    if (
      authenticationValue === Constants.SAMAuthentication ||
      authenticationValue === Constants.SAM2Authentication
    ) {
      retVal = {
        authorizerType: authenticationValue,
        SAMEnabled: true,
        samLoginUrl: Configuration.getConfigurationForAttribute(
          Constants.samLoginUrl,
          configuration
        ),
      };
    }
    logger.logTrace(`Exit checkForSAMAuthentication`);
    return retVal;
  }

  /**@function initiateSAMEnabledLoginSSO
   * @description method to initiate the SAM login SSO flow.
   * @param respObject {any} SAM auth response containing accessToken and refreshToken.
   * @param authResponse {AuthCallResponse} output object populated with the Teamcenter session.
   * @param tokenSid {any} Token session id forwarded to the SSO login request.
   * @param userInfo {AccountInfo} Resolved AAD account info used to populate userAadOid.
   */
  public static async initiateSAMEnabledLoginSSO(
    respObject: any,
    authResponse: AuthCallResponse,
    tokenSid: any,
    userInfo?: AccountInfo
  ): Promise<void> {
    logger.logTrace(`Entered initiateSAMEnabledLoginSSO`);

    if (respObject) {
      const reqBody = {
        samAccessToken: respObject.accessToken,
        samRefreshToken: respObject.refreshToken,
        userAadOid: userInfo?.localAccountId,
        tokenSid: tokenSid || "",
        appType: Constants.appType,
      };
      const tcSSOLoginResponse: ApiResponse = await RequestUtils.callTcTeamsApi(
        Constants.oploginSSO,
        undefined,
        reqBody
      );
      if (!tcSSOLoginResponse.error) {
        authResponse.sessionId = tcSSOLoginResponse.data.sessionId;
        authResponse.userId = tcSSOLoginResponse.data.tcUserId;
        authResponse.userUid = tcSSOLoginResponse.data.tcUserUid;
        authResponse.isTokenRefreshed =
          tcSSOLoginResponse.data.isTokenRefreshed;
        authResponse.locale = tcSSOLoginResponse.data.locale;
        authResponse.siteId = tcSSOLoginResponse.data.siteId;
        authResponse.appDeploymentId = tcSSOLoginResponse.data.appDeploymentId;
        authResponse.tenantId = tcSSOLoginResponse.data.tenantId;
        authResponse.userDisplayName = tcSSOLoginResponse.data.userDisplayName;
        // if (authResponse.isTokenRefreshed) {
        //   authResponse.accessToken = tcSSOLoginResponse.data.accessToken;
        //   authResponse.refreshToken = tcSSOLoginResponse.data.refreshToken;
        // } else {
        //   authResponse.accessToken = respObject.accessToken;
        //   authResponse.refreshToken = respObject.refreshToken;
        // }
        authResponse.ecaId = tcSSOLoginResponse.data.ecaId;
      } else {
        authResponse.error = tcSSOLoginResponse.error;
      }
    }
    logger.logTrace(`Exit initiateSAMEnabledLoginSSO`);
  }
}
